import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ChantierKpis from "@/components/ChantierKpis";
import { ChantierMetrics } from "@/hooks/useChantierMetrics";
import { TrendingUp } from "lucide-react";
import { getRentabilityBadge } from "@/lib/rentabilityBadge";
import { CriticalDayVisualization } from "@/components/gestion-financiere/CriticalDayVisualization";

interface ProfitabilityViewProps {
  metrics: ChantierMetrics | null;
  loading: boolean;
  chantierId: string;
  dateDebutChantier?: string | null;
  dateFinPrevue?: string | null;
  joursRetard?: number; // Jours de retard déclarés
  criticalDelayDays?: number | null; // Nombre de jours de retard avant déficit (calculé par simulation)
}

const ProfitabilityView = ({ metrics, loading, chantierId, dateDebutChantier, dateFinPrevue, joursRetard, criticalDelayDays }: ProfitabilityViewProps) => {
  if (loading) {
    return (
      <Card className="card-premium">
        <CardContent className="pt-16 pb-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des métriques de rentabilité...</p>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card className="card-premium">
        <CardContent className="pt-16 pb-16 text-center">
          <TrendingUp className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-xl font-semibold text-muted-foreground mb-2">
            Aucune donnée de rentabilité disponible
          </p>
          <p className="text-sm text-muted-foreground">
            Les métriques apparaîtront une fois le chantier configuré avec un budget et une équipe.
          </p>
        </CardContent>
      </Card>
    );
  }

  const rentabilityBadgeActuelle = getRentabilityBadge(metrics.profitability_pct || 0);
  const rentabilityBadgeFinale = getRentabilityBadge(metrics.marge_finale_pct || 0);

  // Masquer "Rentabilité Finale Estimée" quand l'écart avec "Marge Actuelle" < 1 point
  const ecartMarge = Math.abs((metrics.profitability_pct || 0) - (metrics.marge_finale_pct || 0));
  const afficherRentabiliteFinale = ecartMarge >= 1;

  return (
    <div className="space-y-6">
      {/* Badge de rentabilité - DEUX BADGES */}
      <Card className="card-premium">
        <CardContent className="pt-4">
          <div className="flex items-start justify-between gap-6">
            {/* GAUCHE : Marge Actuelle */}
            <div className={`${afficherRentabiliteFinale ? 'text-left' : 'text-center'} flex-1`}>
              <h3 className="text-base font-semibold text-white mb-2">Marge Actuelle</h3>
              <div className="text-3xl font-black mb-1">{metrics.profitability_pct?.toFixed(1)}%</div>
              <div className="text-xs text-muted-foreground mb-2">à ce jour</div>
              <Badge className={`text-sm font-bold px-3 py-1 border-2 ${rentabilityBadgeActuelle.bgColor} ${rentabilityBadgeActuelle.color}`}>
                {rentabilityBadgeActuelle.emoji} {rentabilityBadgeActuelle.label}
              </Badge>
            </div>
            {/* DROITE : Rentabilité Finale Estimée + message (masqué si écart < 1 point avec Marge Actuelle) */}
            {afficherRentabiliteFinale && (
              <div className="text-right flex-1">
                <h3 className="text-base font-semibold text-white mb-2">Rentabilité Finale Estimée</h3>
                <div className="text-3xl font-black mb-2">{metrics.marge_finale_pct?.toFixed(1)}%</div>
                <Badge className={`text-sm font-bold px-3 py-1 border-2 ${rentabilityBadgeFinale.bgColor} ${rentabilityBadgeFinale.color}`}>
                  {rentabilityBadgeFinale.emoji} {rentabilityBadgeFinale.label}
                </Badge>
                <div className="mt-3 flex justify-end">
                  <span className={`inline-block px-3 py-1.5 rounded-lg text-xs font-medium ${rentabilityBadgeFinale.bgColor} ${rentabilityBadgeFinale.color}`}>
                    {rentabilityBadgeFinale.message}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Indicateurs clés */}
      <ChantierKpis metrics={metrics} />

      {/* Visualisation du Jour Critique - séparé des KPIs */}
      <div className="mt-12 pt-8 border-t border-border/30">
        <CriticalDayVisualization
          joursEcoules={metrics.jours_ecoules || 0}
          jourCritique={metrics.jour_critique ?? null}
          joursRestantsRentables={metrics.jours_restants_rentables ?? null}
          dateDebutChantier={dateDebutChantier ?? null}
          dateFinPrevue={dateFinPrevue ?? null}
          dureeEstimee={metrics.duree_estimee_jours ?? null}
          joursSuspensionTotal={metrics.jours_suspension_total || 0}
          joursRetard={joursRetard}
          criticalDelayDays={criticalDelayDays}
          margeFinale={metrics.marge_finale}
          coutMoyenJournalier={metrics.cout_moyen_journalier ?? metrics.cout_journalier_equipe}
          dateFinDerniereAffectation={metrics.date_fin_effective ?? null}
        />
      </div>
    </div>
  );
};

export default ProfitabilityView;
